// Settle-sheet prop assembly (gdn-2957).
//
// The drawer was never just chrome: it was the layer that turned a small
// trigger payload ({patientId, encounterId, method, amount}) into the
// eighteen props SettleSheet actually needs — invoiceable line ids,
// writable line ids, charge groups, the unallocated credit, the
// BE-suggested reason, the eligibility copay chip.
//
// Retiring the drawer means that assembly has to live somewhere that
// isn't the drawer. It lives here, and both the drawer and the
// page-anchored PaymentSheetHost consume it, so there is exactly one
// implementation to keep correct.
import { useMemo } from 'react';
import type { DisplayEncounterChargeGroup } from '@features/billing/components/charge-groups';
import { displayGroupsFromPreview } from '@features/billing/components/charge-groups';
import {
  type CreditSuggestion,
  creditSuggestionOf,
} from '@features/billing/utils/creditSuggestion';
import {
  type OpenInvoiceTarget,
  openInvoiceTargeting,
} from '@features/billing/utils/openInvoiceTargeting';
// Direct file import (no feature barrel) — same source the Eligibility tab uses.
import { useEligibilityBadgeState } from '@features/eligibility-checks/hooks/useEligibilityBadgeState';
import { fixedCopayAmount } from '@features/patient-coverages/utils/patientCost';
import type { ChargeLinePreviewLine } from '@store/generated/billing-v2';

import type { SettleMethod } from '../types';
import type { CopaySuggestion } from '../utils/allocationEngine';

import { useLedgerData } from './useLedgerData';

interface UseSettleSheetPropsArgs {
  patientId: string;
  encounterId?: string | null;
  /** Exact charge lines the settle action should cover, when the caller knows. */
  chargeLineIds?: string[];
}

export interface SettleSheetDerived {
  /** Full ledger read — the drawer still renders its own chrome off this. */
  ledger: ReturnType<typeof useLedgerData>;
  /** Props destined for `<SettleSheet>`, minus the caller's own presets. */
  sheet: {
    suggestedReason?: string;
    invoiceChargeLineIds?: string[];
    writableChargeLineIds?: string[];
    invoiceableAmountCents?: number;
    unallocatedCreditCents: number;
    chargeGroups: DisplayEncounterChargeGroup[];
    reminderInvoiceId?: string;
    /** The invoice a card charge should settle, or null when charging
     * one is not the right act. Never undefined here: the hook always
     * has an opinion, because it can see the ledger. */
    openInvoiceTarget: OpenInvoiceTarget | null;
    /** Unapplied credit worth mentioning before collecting more, or null.
     * Derived here rather than passed by callers so every surface that
     * opens the sheet gets it — the same reason openInvoiceTarget is
     * derived here (gdn-3169). */
    creditSuggestion: CreditSuggestion | null;
    copaySuggestion: CopaySuggestion | null;
    /** Charge-line preview still in flight — the sheet holds space for it. */
    chargeGroupsLoading: boolean;
    /** Distinguishes an unresolved preview from a completed empty result. */
    chargeGroupsReady: boolean;
    chargeGroupsError: boolean;
    onRetryChargeGroups: () => void;
  };
}

/**
 * Assemble everything SettleSheet needs for a given patient/encounter scope.
 *
 * Pure derivation over `useLedgerData` + the eligibility snapshot — no
 * drawer state, no tab state, nothing that assumes a side pane.
 */
export function useSettleSheetProps({
  patientId,
  encounterId,
  chargeLineIds,
}: UseSettleSheetPropsArgs): SettleSheetDerived {
  const ledger = useLedgerData({ patientId, encounterId, chargeLineIds });

  // Eligibility snapshot — same RTK-cached source the Eligibility tab reads.
  // Feeds the settle sheet's copay suggestion chip (amount + checked-at);
  // the production EligibilityCheckResponse doesn't expose a plan name.
  const { eligibilityData } = useEligibilityBadgeState({
    patientId,
    encounterId: encounterId ?? undefined,
  });
  const copaySuggestion = useMemo<CopaySuggestion | null>(() => {
    const amount = fixedCopayAmount(
      eligibilityData?.patient_cost,
      eligibilityData?.patient_insurance_coverage_id,
    );
    if (amount === undefined) return null;
    return {
      amountCents: amount,
      checkedAt: eligibilityData?.last_checked_at ?? null,
    };
  }, [eligibilityData]);

  const preview = ledger.preview;
  const openInvoiceIds = ledger.openInvoiceIds;
  const openInvoices = ledger.openInvoices;
  const accountAllocatableCreditCents = ledger.accountAllocatableCreditCents;
  const hasCreditTarget = ledger.hasCreditTarget;
  const isPreviewLoading = ledger.isPreviewLoading;
  const previewError = ledger.previewError;
  const retryPreview = ledger.retryPreview;

  const sheet = useMemo(() => {
    const previewLines = preview?.encounters?.flatMap(encounter => encounter.lines ?? []);
    const explicitChargeLineIds =
      chargeLineIds && chargeLineIds.length > 0 ? new Set(chargeLineIds) : null;
    const inScope = (line: ChargeLinePreviewLine) =>
      !explicitChargeLineIds || explicitChargeLineIds.has(line.charge_line_id);

    const invoiceableChargeLineIds =
      previewLines
        ?.filter(line => isInvoiceable(line) && inScope(line))
        .map(line => line.charge_line_id) ?? chargeLineIds;

    const invoiceableLineAmountCents = previewLines?.reduce((total, line) => {
      if (!isInvoiceable(line) || !inScope(line)) return total;
      return total + (line.patient_due_cents ?? 0);
    }, 0);

    // Write-off may target invoiced lines too, so this set is deliberately
    // wider than the invoiceable one. Cash payments allocate from it as well.
    const selectableWriteOffChargeLineIds =
      previewLines
        ?.filter(line => isSettleable(line) && inScope(line))
        .map(line => line.charge_line_id) ?? chargeLineIds;

    // Everything owed already sits on an open invoice, so a card charge
    // must settle THAT invoice rather than mint a second one (gdn-3154).
    // Derived here, not at one call site: the gated sheet and the legacy
    // drawer render the same SettleSheet, and fixing only the surface
    // that reported the bug leaves the other one double-billing.
    //
    // `preview` must have arrived first. Without it invoiceableChargeLineIds
    // falls back to the caller's ids, and an empty list would read as
    // "nothing left to invoice" when the truth is "not known yet".
    return {
      creditSuggestion: creditSuggestionOf(accountAllocatableCreditCents, hasCreditTarget),
      openInvoiceTarget: openInvoiceTargeting(
        openInvoices,
        !!preview && (invoiceableChargeLineIds?.length ?? 0) === 0,
      ),
      suggestedReason: preview?.suggested_reason,
      invoiceChargeLineIds: invoiceableChargeLineIds,
      writableChargeLineIds: selectableWriteOffChargeLineIds,
      invoiceableAmountCents: preview?.total_due_cents ?? invoiceableLineAmountCents,
      unallocatedCreditCents: preview?.unallocated_credit_cents ?? 0,
      chargeGroups: displayGroupsFromPreview(preview),
      reminderInvoiceId: openInvoiceIds[0],
      copaySuggestion,
      chargeGroupsLoading: isPreviewLoading,
      chargeGroupsReady: !!preview,
      chargeGroupsError: Boolean(previewError),
      onRetryChargeGroups: retryPreview,
    };
  }, [
    preview,
    chargeLineIds,
    openInvoiceIds,
    openInvoices,
    accountAllocatableCreditCents,
    hasCreditTarget,
    copaySuggestion,
    isPreviewLoading,
    previewError,
    retryPreview,
  ]);

  return { ledger, sheet };
}

/**
 * Whether a new invoice may bill the line. The server decides (gdn-4242):
 * a line held after a refund still owes money, but it is never invoiceable.
 * The old due-and-not-invoiced rule stands in only when a server leaves the
 * flag out.
 */
function isInvoiceable(line: ChargeLinePreviewLine): boolean {
  return line.invoiceable ?? ((line.patient_due_cents ?? 0) > 0 && !line.already_invoiced);
}

/**
 * Whether the sheet may pay or write off the line. A line held after a
 * refund is decided by Bill patient or Write off on the visit, never here:
 * the server refuses a payment on it (gdn-4242).
 */
function isSettleable(line: ChargeLinePreviewLine): boolean {
  return (line.patient_due_cents ?? 0) > 0 && !line.held_after_refund;
}

/**
 * Opening amount for the settle sheet.
 *
 * Invoice-like methods treat the amount as a NEW ad-hoc charge — selected
 * receipt lines add on top inside the sheet — so only an explicit
 * caller-provided amount may prefill it. Prefilling the balance there
 * mints a duplicate charge (double bill).
 */
export function settleAmountFor(
  method: SettleMethod | undefined,
  defaultAmountCents: number | undefined,
  balanceCents: number,
): number {
  if (method === 'invoice' || method === 'card_on_file') {
    return defaultAmountCents ?? 0;
  }
  return defaultAmountCents ?? (balanceCents > 0 ? balanceCents : 0);
}
